import { BrandingSectionView } from "@cma/presentation-generation-logic/models/section-view";
import { textFormatter } from "@shared/utils/text-formatter";
import { useCallback, useState } from "react";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { socialMediaConfig } from "./helpers/social-media-helpers";
import { SinglePhotoUploader } from "@cma/shared/components/SinglePhotoUploader";
import { compressImage } from "@shared/utils/compress-image";
import { useUploadPresentationSectionImage } from "@cma/shared/queries/cma/useUploadPresentationSectionImage";
import { BrandingSectionConfig } from "@cma/presentation-generation-logic/models/section-config";
import { PresentationSectionProps } from "../PresentationSectionProps";
import { EditableInput } from "../../shared/EditableInput";
import { EditableText } from "../../shared/EditableText";
import {
  AgentAvatarContainer,
  AgentInfoContent,
  BrokerageInfoContainer,
  BrokerageLogoContainer,
  ContactCard,
  ContactInfoContainer,
  ContactInfoItem,
  LeftCardInfo,
  NoAgentAvatarPlaceholder,
  NoOfficeLogoPlaceholder,
  SectionEditWrapper,
  SocialMediasContainer,
} from "./Branding.styles";

interface Props
  extends PresentationSectionProps<BrandingSectionView, BrandingSectionConfig> {
  displayMode: "mobile" | "desktop" | "print" | "tablet";
}

export const BrandingEdit = (props: Props) => {
  const { sectionView, sectionConfig, sourceData, onUpdateSection } = props;
  const {
    id,
    agentName,
    agentEmail,
    agentTitle,
    agentPhoneNumber,
    agentAvatar,
    officeName,
    officeLogo,
    website,
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
  } = sectionView;
  const [editModeField, setEditModeField] = useState<string | null>(null);

  const socialMediaFields = {
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
  };

  const prettyUrl = textFormatter.formatPrettyUrl(website ?? null);

  const uploadSectionAgentAvatarMutation = useUploadPresentationSectionImage({
    onSuccess: (data) => {
      onUpdateSection({
        sectionConfig: { ...sectionConfig, agentAvatar: data.url },
      });
    },
  });

  const uploadSectionAgentAvatar = useCallback(
    async (file: File) => {
      const compressed = await compressImage(file, { preset: "sm" });
      uploadSectionAgentAvatarMutation.mutate({
        id: sourceData.id,
        sectionId: sectionConfig.id,
        file: compressed,
      });
    },
    [uploadSectionAgentAvatarMutation, sectionConfig, sourceData],
  );

  const deleteSectionAgentAvatar = useCallback(() => {
    onUpdateSection({
      sectionConfig: { ...sectionConfig, agentAvatar: null },
    });
  }, [onUpdateSection, sectionConfig]);

  const uploadSectionOfficeLogoMutation = useUploadPresentationSectionImage({
    onSuccess: (data) => {
      onUpdateSection({
        sectionConfig: { ...sectionConfig, officeLogo: data.url },
      });
    },
  });

  const deleteSectionOfficeLogo = useCallback(() => {
    onUpdateSection({
      sectionConfig: { ...sectionConfig, officeLogo: null },
    });
  }, [onUpdateSection, sectionConfig]);

  const onUpdateBrandingField = useCallback(
    (fieldName: keyof BrandingSectionConfig, value: string) => {
      onUpdateSection({
        sectionConfig: { ...sectionConfig, [fieldName]: value },
      });
      setEditModeField(null);
    },
    [onUpdateSection, sectionConfig],
  );

  const uploadSectionOfficeLogo = useCallback(
    async (file: File) => {
      const compressed = await compressImage(file, { preset: "sm" });
      uploadSectionOfficeLogoMutation.mutate({
        id: sourceData.id,
        sectionId: sectionConfig.id,
        file: compressed,
      });
    },
    [uploadSectionOfficeLogoMutation, sectionConfig, sourceData],
  );

  return (
    <SectionEditWrapper id={id}>
      <ContactCard>
        <LeftCardInfo>
          <BrokerageInfoContainer>
            <BrokerageLogoContainer>
              <SinglePhotoUploader
                onFileAccepted={uploadSectionOfficeLogo}
                onFileRemoved={officeLogo ? deleteSectionOfficeLogo : undefined}
                maxSize={5 * 1024 * 1024} // 5MB
                isLoading={uploadSectionOfficeLogoMutation.isPending}
                serverError={uploadSectionOfficeLogoMutation.error}
                resetServerError={uploadSectionOfficeLogoMutation.reset}
              >
                {officeLogo ? (
                  <img src={officeLogo} alt={officeName ?? ""} />
                ) : (
                  <NoOfficeLogoPlaceholder>
                    <PhotoCameraIcon fontSize="small" />
                  </NoOfficeLogoPlaceholder>
                )}
              </SinglePhotoUploader>
            </BrokerageLogoContainer>
            {editModeField !== "officeName" && !!officeName ? (
              <EditableText
                variant="h5"
                onClick={() => setEditModeField("officeName")}
              >
                {officeName}
              </EditableText>
            ) : (
              <EditableInput
                value={officeName}
                type="text"
                name="officeName"
                variant="h5"
                autoFocus={editModeField === "officeName"}
                onSave={(value) => onUpdateBrandingField("officeName", value)}
                onCancel={() => setEditModeField(null)}
                styles={{ maxWidth: "100%" }}
                placeholder="Office Name"
              />
            )}
          </BrokerageInfoContainer>

          <AgentInfoContent>
            {editModeField !== "agentName" && !!agentName ? (
              <EditableText
                variant="h6"
                onClick={() => setEditModeField("agentName")}
              >
                {agentName}
              </EditableText>
            ) : (
              <EditableInput
                value={agentName}
                type="text"
                name="agentName"
                onSave={(value) => onUpdateBrandingField("agentName", value)}
                variant="h6"
                autoFocus={editModeField === "agentName"}
                onCancel={() => setEditModeField(null)}
                styles={{ maxWidth: "100%" }}
                placeholder="Agent Name"
              />
            )}

            {editModeField !== "agentTitle" && !!agentTitle ? (
              <EditableText
                variant="body2"
                onClick={() => setEditModeField("agentTitle")}
              >
                {agentTitle}
              </EditableText>
            ) : (
              <EditableInput
                value={agentTitle}
                type="text"
                name="agentTitle"
                onSave={(value) => onUpdateBrandingField("agentTitle", value)}
                variant="body2"
                autoFocus={editModeField === "agentTitle"}
                onCancel={() => setEditModeField(null)}
                styles={{ maxWidth: "100%" }}
                placeholder="Agent Title"
              />
            )}

            <ContactInfoContainer>
              <ContactInfoItem>
                <MailIcon fontSize="small" />
                {editModeField !== "agentEmail" && !!agentEmail ? (
                  <EditableText onClick={() => setEditModeField("agentEmail")}>
                    {agentEmail}
                  </EditableText>
                ) : (
                  <EditableInput
                    value={agentEmail}
                    type="email"
                    name="agentEmail"
                    onSave={(value) =>
                      onUpdateBrandingField("agentEmail", value)
                    }
                    variant="body2"
                    autoFocus={editModeField === "agentEmail"}
                    onCancel={() => setEditModeField(null)}
                    styles={{ maxWidth: "100%" }}
                    placeholder="Agent Email"
                  />
                )}
              </ContactInfoItem>

              <ContactInfoItem>
                <PhoneIcon fontSize="small" />

                {editModeField !== "agentPhoneNumber" && !!agentPhoneNumber ? (
                  <EditableText
                    onClick={() => setEditModeField("agentPhoneNumber")}
                  >
                    {textFormatter.formatPhoneNumber(agentPhoneNumber)}
                  </EditableText>
                ) : (
                  <EditableInput
                    value={agentPhoneNumber}
                    type="phone"
                    name="agentPhoneNumber"
                    onSave={(value) =>
                      onUpdateBrandingField("agentPhoneNumber", value)
                    }
                    variant="body2"
                    autoFocus={editModeField === "agentPhoneNumber"}
                    onCancel={() => setEditModeField(null)}
                    styles={{ maxWidth: "100%" }}
                    placeholder="Agent Phone Number"
                  />
                )}
              </ContactInfoItem>

              <ContactInfoItem>
                <LanguageIcon fontSize="small" />
                {editModeField !== "website" && !!website && !!prettyUrl ? (
                  <EditableText onClick={() => setEditModeField("website")}>
                    {prettyUrl}
                  </EditableText>
                ) : (
                  <EditableInput
                    value={website}
                    type="text"
                    name="website"
                    onSave={(value) => onUpdateBrandingField("website", value)}
                    variant="body2"
                    autoFocus={editModeField === "website"}
                    onCancel={() => setEditModeField(null)}
                    styles={{ maxWidth: "100%" }}
                    placeholder="Website"
                  />
                )}
              </ContactInfoItem>
            </ContactInfoContainer>
          </AgentInfoContent>

          <SocialMediasContainer>
            {Object.entries(socialMediaConfig).map(
              ([key, { label, name, SvgIcon, extractHandle, handleToUrl }]) => {
                const handle = extractHandle(socialMediaFields[name] || "");
                return (
                  <ContactInfoItem key={key}>
                    <SvgIcon fontSize="small" />
                    {editModeField !== name ? (
                      <EditableText
                        onClick={() => setEditModeField(name)}
                        variant={handle?.trim() ? "body1" : "body2"}
                        fontStyle={!handle?.trim() ? "italic" : "normal"}
                      >
                        {handle?.trim() ? handle : "Empty"}
                      </EditableText>
                    ) : (
                      <EditableInput
                        value={handle}
                        type="text"
                        name={name}
                        onSave={(value) =>
                          onUpdateBrandingField(name, handleToUrl(value))
                        }
                        variant="body2"
                        autoFocus={editModeField === name}
                        onCancel={() => setEditModeField(null)}
                        styles={{ maxWidth: "100%" }}
                        placeholder={label}
                      />
                    )}
                  </ContactInfoItem>
                );
              },
            )}
          </SocialMediasContainer>
        </LeftCardInfo>

        <AgentAvatarContainer>
          <SinglePhotoUploader
            onFileAccepted={uploadSectionAgentAvatar}
            onFileRemoved={agentAvatar ? deleteSectionAgentAvatar : undefined}
            maxSize={5 * 1024 * 1024} // 5MB
            isLoading={uploadSectionAgentAvatarMutation.isPending}
            serverError={uploadSectionAgentAvatarMutation.error}
            resetServerError={uploadSectionAgentAvatarMutation.reset}
          >
            {agentAvatar ? (
              <img src={agentAvatar} alt={agentName ?? ""} />
            ) : (
              <NoAgentAvatarPlaceholder>
                <PhotoCameraIcon />
              </NoAgentAvatarPlaceholder>
            )}
          </SinglePhotoUploader>
        </AgentAvatarContainer>
      </ContactCard>
    </SectionEditWrapper>
  );
};
